import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import AnnouncementIcon from '@mui/icons-material/Announcement';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import PeopleIcon from '@mui/icons-material/People';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BadgeIcon from '@mui/icons-material/Badge';
import api from '../../utils/api';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';

export default function MemberDashboard() {
  const [member, setMember] = useState(null);
  const [events, setEvents] = useState([]);
  const [elections, setElections] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [duesStatus, setDuesStatus] = useState({ semesters: [] });
  const [cardApproved, setCardApproved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [showNewExecutivesBanner, setShowNewExecutivesBanner] = useState(false);
  const navigate = useNavigate();

  const formatAnnouncementDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  useEffect(() => {
    const token = localStorage.getItem('memberToken');
    if (!token) {
      navigate('/member-portal/login');
      return;
    }

    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      // Set auth token for member portal requests
      const token = localStorage.getItem('memberToken');
      api.defaults.headers.common['x-auth-token'] = token;

      const [profileRes, eventsRes, electionsRes, announcementsRes, cardRes, transitionRes, duesRes] = await Promise.all([
        api.get('/member-portal/profile'),
        api.get('/member-portal/events'),
        api.get('/member-portal/elections'),
        api.get('/member-portal/announcements?limit=5'),
        api.get('/member-portal/card').catch(() => ({ data: { approved: false } })),
        api.get('/member-portal/executives/transition-status').catch(() => ({ data: { data: { show_transition: false } } })),
        api.get('/member-portal/dues-status').catch(() => ({ data: { data: { semesters: [] } } })),
      ]);

      setMember(profileRes.data.data);
      setEvents(eventsRes.data.data || []);
      setElections(electionsRes.data.data || []);
      setAnnouncements(announcementsRes.data.data || []);
      setCardApproved(cardRes.data?.approved === true);
      setShowNewExecutivesBanner(transitionRes.data?.data?.show_transition === true);
      setDuesStatus(duesRes.data?.data || { semesters: [] });
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('memberToken');
        localStorage.removeItem('member');
        navigate('/member-portal/login');
      } else {
        setError(err.response?.data?.message || 'Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mb: 4 }}>
        <Paper sx={{ p: 2.5, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Skeleton variant="circular" width={56} height={56} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={32} />
              <Skeleton variant="text" width="40%" height={24} />
            </Box>
            <Skeleton variant="rounded" width={80} height={36} sx={{ mr: 1 }} />
            <Skeleton variant="rounded" width={80} height={36} />
          </Box>
        </Paper>
        <Skeleton variant="text" width={140} height={24} sx={{ mb: 1.5 }} />
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card variant="outlined">
                <CardContent>
                  <Skeleton variant="text" width="70%" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="90%" />
                  <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="text" width={100} height={24} sx={{ mb: 1.5 }} />
        <Grid container spacing={2}>
          {[1, 2].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Card variant="outlined">
                <CardContent>
                  <Skeleton variant="text" width="60%" height={28} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="80%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const firstName = member?.first_name || 'Member';

  return (
    <Box>
      <Container
        maxWidth="md"
        sx={{
          mb: 4,
          px: { xs: 2, sm: 3 },
          pt: { xs: 2, sm: 3 },
        }}
      >
        {showNewExecutivesBanner && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => navigate('/member-portal/executives')}
              >
                View New Team
              </Button>
            }
          >
            New executives have been elected! Meet your new leadership team.
          </Alert>
        )}

        {/* Welcome & Quick Actions */}
        <Paper
          sx={{
            p: 2.5,
            mb: 3,
            background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.08) 0%, rgba(46, 125, 50, 0.02) 100%)',
            border: '1px solid',
            borderColor: 'primary.light',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
              <MemberProfileAvatar member={member} size={56} sx={{ flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Hello, {firstName}
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {member?.email}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: { xs: 'stretch', sm: 'flex-start' },
              }}
            >
              <Button
                variant="contained"
                size="medium"
                startIcon={<PersonIcon />}
                onClick={() => navigate('/member-portal/profile')}
              >
                Profile
              </Button>
              <Button
                variant={cardApproved ? 'contained' : 'outlined'}
                size="medium"
                startIcon={<BadgeIcon />}
                onClick={() => navigate('/member-portal/card')}
                disabled={!cardApproved}
              >
                ID Card
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* What's happening – Engagement */}
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, letterSpacing: 1.2 }}>
          What&apos;s happening
        </Typography>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EventIcon color="primary" fontSize="small" />
                  Upcoming Events
                </Typography>
                {events.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No upcoming events. Check back later.
                  </Typography>
                ) : (
                  <>
                    <List dense disablePadding>
                      {events.slice(0, 3).map((event) => (
                        <ListItem
                          key={event.id}
                          button
                          onClick={() => navigate(`/member-portal/events/${event.id}`)}
                          sx={{ px: 0 }}
                        >
                          <ListItemText
                            primary={event.name}
                            secondary={new Date(event.date).toLocaleDateString()}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                          {event.attendance_confirmed && (
                            <Chip label="Confirmed" size="small" color="success" />
                          )}
                        </ListItem>
                      ))}
                    </List>
                    {events.length > 3 && (
                      <Button
                        fullWidth
                        size="small"
                        onClick={() => navigate('/member-portal/events')}
                        sx={{ mt: 1 }}
                      >
                        View All Events
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HowToVoteIcon color="primary" fontSize="small" />
                  Elections
                </Typography>
                {elections.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No elections at the moment.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {elections.map((election) => (
                      <ListItem
                        key={election.id}
                        button
                        onClick={() => navigate(`/member-portal/elections/${election.id}/vote`)}
                        sx={{ px: 0 }}
                      >
                        <ListItemText
                          primary={election.name}
                          secondary={
                            election.status === 'active'
                              ? `Ends: ${new Date(election.end_date).toLocaleDateString()}`
                              : `Opens: ${new Date(election.start_date).toLocaleDateString()}`
                          }
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                        {election.is_paused && (
                          <Chip label="Paused" size="small" color="warning" variant="outlined" />
                        )}
                        {election.status === 'active' && !election.is_paused && election.positions?.some((p) => p.has_voted) && (
                          <Chip label="Voted" size="small" color="success" />
                        )}
                        {election.status === 'upcoming' && !election.is_paused && (
                          <Chip label="Coming soon" size="small" variant="outlined" />
                        )}
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Membership Dues Status */}
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, letterSpacing: 1.2 }}>
          Membership Dues
        </Typography>
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 18, color: 'primary.main' }} />
              Dues by Semester
            </Typography>
            {!duesStatus.semesters?.length ? (
              <Typography variant="body2" color="text.secondary">
                No semester data available.
              </Typography>
            ) : (() => {
              const paidSemesters = (duesStatus.semesters || []).filter((s) => s.paid);
              if (paidSemesters.length === 0) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    No dues recorded as paid yet. Paid semesters will appear here once recorded by admin.
                  </Typography>
                );
              }
              return (
              <List dense disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {paidSemesters
                  .sort((a, b) => a.year - b.year || (a.semester > b.semester ? 1 : -1))
                  .map((s) => {
                    const semNum = s.semester?.replace('Semester ', '') || '?';
                    return (
                      <ListItem
                        key={`${s.year}-${s.semester}`}
                        sx={{
                          flexDirection: 'column',
                          alignItems: 'stretch',
                          py: 1.25,
                          px: 1.5,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} color="text.primary">
                          Semester {semNum} of Year {s.year} dues
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: 'success.main',
                              color: 'white',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 12,
                              fontWeight: 700,
                            }}
                          >
                            ✓
                          </Box>
                          <Typography variant="body2" color="success.main" fontWeight={500}>
                            Paid
                          </Typography>
                        </Box>
                      </ListItem>
                    );
                  })}
              </List>
              );
            })()}
          </CardContent>
        </Card>

        {/* Information – Announcements & Executives */}
        <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5, letterSpacing: 1.2 }}>
          Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AnnouncementIcon color="primary" fontSize="small" />
                  Recent Announcements
                </Typography>
                {announcements.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No announcements yet.
                  </Typography>
                ) : (
                  <>
                    <List dense disablePadding>
                      {announcements.map((announcement) => (
                        <ListItem
                          key={announcement.id}
                          button
                          onClick={() => setSelectedAnnouncement(announcement)}
                          sx={{ px: 0 }}
                        >
                          <ListItemText
                            primary={announcement.title}
                            secondary={new Date(announcement.created_at).toLocaleDateString()}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => navigate('/member-portal/announcements')}
                      sx={{ mt: 1 }}
                    >
                      View All Announcements
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon color="primary" fontSize="small" />
                  Our Executives
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  View your current executive team and their terms.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  size="small"
                  startIcon={<PeopleIcon />}
                  onClick={() => navigate('/member-portal/executives')}
                >
                  View Executives
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Dialog
        open={!!selectedAnnouncement}
        onClose={() => setSelectedAnnouncement(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedAnnouncement && (
          <>
            <DialogTitle>{selectedAnnouncement.title || 'Announcement'}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {formatAnnouncementDate(selectedAnnouncement.created_at)}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedAnnouncement.message || 'No content.'}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedAnnouncement(null)}>Close</Button>
              <Button
                variant="contained"
                onClick={() => {
                  setSelectedAnnouncement(null);
                  navigate('/member-portal/announcements');
                }}
              >
                View All Announcements
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
